import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { date } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Plus, Trash2, RotateCcw } from "lucide-react";

export default function BankHolidays() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date: "", name: "" });
  const load = useCallback(() => api.get("/hr/holidays", { params: { year, include_removed: true } }).then((r) => setRows(r.data)), [year]);
  useEffect(() => { load(); }, [load]);
  const act = async (p, msg) => { try { await p; toast.success(msg); load(); } catch (e) { toast.error(apiErr(e)); } };
  const add = () => act(api.post("/hr/holidays", f), "Closure day added").then(() => setF({ date: "", name: "" }));
  const wd = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IE", { weekday: "short" });
  return (
    <div className="mt-4 max-w-2xl">
      <div className="flex items-center gap-2 mb-3">
        <Button size="icon" variant="outline" onClick={() => setYear(year - 1)} data-testid="bh-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="font-heading text-xl font-extrabold w-16 text-center" data-testid="bh-year">{year}</span>
        <Button size="icon" variant="outline" onClick={() => setYear(year + 1)} data-testid="bh-next"><ChevronRight className="w-4 h-4" /></Button>
        <p className="text-xs text-muted-foreground ml-2">Irish public holidays are added automatically and are not counted as leave.</p>
      </div>
      <div className="card-industrial p-3 mb-3 grid gap-2 sm:grid-cols-[160px_1fr_auto]">
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="bh-date" />
        <Input placeholder="Closure name, e.g. Christmas shutdown" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bh-name" />
        <Button onClick={add} disabled={!f.date} data-testid="bh-add"><Plus className="w-4 h-4 mr-1" />Add closure day</Button>
      </div>
      <div className="card-industrial divide-y divide-border" data-testid="bh-list">
        {rows.map((h) => (
          <div key={h.date + h.kind} className={`px-4 py-2.5 flex items-center justify-between gap-3 ${h.removed ? "opacity-50" : ""}`} data-testid={`bh-row-${h.date}`}>
            <div className="flex items-center gap-3">
              <span className="font-mono-num text-sm w-28">{wd(h.date)} {date(h.date)}</span>
              <span className={`font-semibold ${h.removed ? "line-through" : ""}`}>{h.name}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded font-mono uppercase ${h.kind === "closure" ? "bg-sky-500/20 text-sky-300" : "bg-emerald-500/20 text-emerald-300"}`}>{h.kind === "closure" ? "Closure" : "Bank holiday"}</span>
            </div>
            {h.removed
              ? <Button size="sm" variant="ghost" onClick={() => act(api.post(`/hr/holidays/${h.date}/restore`), "Holiday restored")} data-testid={`bh-restore-${h.date}`}><RotateCcw className="w-4 h-4 mr-1" />Restore</Button>
              : <Button size="sm" variant="ghost" onClick={() => act(api.delete(`/hr/holidays/${h.date}`), "Removed")} data-testid={`bh-remove-${h.date}`} className="text-red-400"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
